import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAPI from '../../utils/api';
import AppLoader from '../../shared/AppLoader';
import ConfirmationDialog from '../Modals/ConfirmationDialog';
import { useSnackbar } from 'notistack';
import { useRoleCheck } from '../../utils/useRoleCheck';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
const TAB_CONFIG = [
  { key: 'popup',           label: 'Popup Images' },
  { key: 'logo',            label: 'Logos' },
  { key: 'lootbox',         label: 'Lootbox Images' },
  { key: 'scratch_overlay', label: 'Scratch Overlays' },
  { key: 'widget',          label: 'Widget Images' },
];


/* ── Animations ─────────────────────────────────────────────── */

const dashAnimate = keyframes`
  to { stroke-dashoffset: -20; }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(233, 86, 43, 0.15); }
  50%       { box-shadow: 0 0 0 8px rgba(233, 86, 43, 0); }
`;

/* ── Page ───────────────────────────────────────────────────── */

const Page = styled.div`
  padding: 0 32px 32px;
  max-width: 1000px;
`;

/* ── Sub-navigation + count row ─────────────────────────────── */

const TabBar = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 2px solid #efefef;
  margin-bottom: 24px;
`;

const TabNav = styled.div`
  display: flex;
  flex: 1;
  overflow-x: auto;
`;

const TabItem = styled.button`
  background: none;
  border: none;
  padding: 14px 22px;
  font-size: 0.95rem;
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  color: ${(p) => (p.$active ? '#e9562b' : '#777')};
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  transition: color 0.15s;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 2px;
    background: #e9562b;
    opacity: ${(p) => (p.$active ? 1 : 0)};
    transition: opacity 0.15s;
  }

  &:hover { color: #e9562b; }
`;

const CountBadge = styled.span`
  font-size: 0.75rem;
  color: #aaa;
  white-space: nowrap;
  padding-left: 16px;
  flex-shrink: 0;
`;

/* ── Upload zone ────────────────────────────────────────────── */

const UploadZone = styled.div`
  border: 2px dashed ${(p) => (p.$dragging ? '#e9562b' : '#ddd')};
  border-radius: 12px;
  background: ${(p) =>
    p.$dragging
      ? 'rgba(233, 86, 43, 0.06)'
      : 'linear-gradient(135deg, rgba(233,86,43,0.02) 0%, rgba(255,255,255,1) 100%)'};
  padding: 22px 28px;
  display: flex;
  align-items: center;
  gap: 18px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, transform 0.2s;
  margin-bottom: 28px;
  transform: ${(p) => (p.$dragging ? 'scale(1.01)' : 'scale(1)')};

  ${(p) =>
    p.$dragging &&
    css`
      animation: ${glowPulse} 1.2s ease-in-out infinite;
    `}

  &:hover {
    border-color: #e9562b;
    background: rgba(233, 86, 43, 0.04);
  }
`;

const UploadIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(233, 86, 43, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e9562b;
  flex-shrink: 0;
  svg { font-size: 1.5rem; }
`;

const UploadText = styled.div`
  flex: 1;
`;

const UploadMain = styled.p`
  margin: 0 0 3px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
  span { color: #e9562b; text-decoration: underline; }
`;

const UploadHint = styled.p`
  margin: 0;
  font-size: 0.74rem;
  color: #bbb;
  line-height: 1.5;
`;

const HiddenInput = styled.input`display: none;`;


/* ── Grid & Cards ───────────────────────────────────────────── */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
`;

const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.05) 70%);
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 8px;
  gap: 5px;
`;

const CardAction = styled.button`
  background: rgba(255,255,255,0.93);
  border: none;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #444;
  backdrop-filter: blur(4px);
  transition: background 0.15s, color 0.15s, transform 0.15s;
  svg { font-size: 0.95rem; }

  &:hover { background: #fff; transform: scale(1.08); }
  &.danger:hover { color: #d32f2f; }
  &.copy:hover { color: #e9562b; }
`;

const Card = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: #f0f0f0;
  aspect-ratio: 1 / 1;
  border: 1px solid #f0f0f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.07);
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.25s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.13);
    img { transform: scale(1.04); }
  }

  &:hover ${CardOverlay} { opacity: 1; }
`;

/* ── Empty state ────────────────────────────────────────────── */

const EmptyState = styled.div`
  text-align: center;
  padding: 56px 24px;
  color: #ccc;
  svg { font-size: 3rem; margin-bottom: 14px; display: block; margin-inline: auto; opacity: 0.5; }
  p { margin: 0; font-size: 0.88rem; color: #bbb; }
`;

/* ── ImageCard component ────────────────────────────────────── */

function ImageCard({ image, onDelete }) {
  const { enqueueSnackbar } = useSnackbar();
  const [inUseError, setInUseError] = useState(null);
  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);

  const copyUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(image.url).then(() =>
      enqueueSnackbar('URL copied to clipboard', { variant: 'success' })
    );
  };

  const handleInitialDelete = async () => {
    const result = await onDelete(image.id, { dryRun: false });
    if (result && result.inUse) {
      setInUseError(result.message);
      setForceConfirmOpen(true);
    }
  };

  const handleForceDelete = async () => {
    setForceConfirmOpen(false);
    setInUseError(null);
    await onDelete(image.id, { force: true });
  };

  const deleteTrigger = ({ onClick }) => (
    <CardAction className="danger" title="Delete" onClick={(e) => { e.stopPropagation(); onClick(e); }}>
      <DeleteOutlineIcon />
    </CardAction>
  );

  const ForceDeleteTrigger = () => null;

  return (
    <Card>
      <img src={image.modal_url || image.url} alt="" loading="lazy" />
      <CardOverlay>
        <CardAction className="copy" title="Copy URL" onClick={copyUrl}>
          <ContentCopyIcon />
        </CardAction>
        <ConfirmationDialog onConfirm={handleInitialDelete} component={deleteTrigger}>
          Delete this asset? This cannot be undone.
        </ConfirmationDialog>
        {forceConfirmOpen && (
          <ConfirmationDialog
            onConfirm={handleForceDelete}
            component={ForceDeleteTrigger}
            defaultOpen
            onClose={() => { setForceConfirmOpen(false); setInUseError(null); }}
          >
            {inUseError || 'This image is used in one or more popups.'} Are you sure you want to delete it anyway?
          </ConfirmationDialog>
        )}
      </CardOverlay>
    </Card>
  );
}

/* ── Main component ─────────────────────────────────────────── */

export default function Assets() {
  const API = useAPI();
  const { enqueueSnackbar } = useSnackbar();
  const handleRoleCheck = useRoleCheck(API.currentRole);
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab]   = useState('popup');
  const [images, setImages]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [dragging, setDragging]     = useState(false);

  const isWidgetTab = activeTab === 'widget';

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setImages([]);
    try {
      const [ok, data] = isWidgetTab
        ? await API.getWidgetImageLibrary()
        : await API.getImageLibrary(activeTab);
      if (ok) setImages(data.image_uploads || []);
      else enqueueSnackbar('Failed to load assets', { variant: 'error' });
    } catch {
      enqueueSnackbar('Failed to load assets', { variant: 'error' });
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const uploadFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      enqueueSnackbar('Please upload a valid image file', { variant: 'error' });
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      enqueueSnackbar('File size exceeds 1 MB. Please upload a smaller image.', { variant: 'error' });
      return;
    }
    if (handleRoleCheck()) return;
    setUploading(true);
    try {
      const [ok, data] = isWidgetTab
        ? await API.uploadWidgetImage(file)
        : await API.uploadImage(API.siteId, file, activeTab);
      if (ok) {
        setImages((prev) => [data, ...prev]);
        enqueueSnackbar('Asset uploaded successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(data?.error || data?.message || 'Upload failed', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Upload failed', { variant: 'error' });
    }
    setUploading(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDelete = async (id, options = {}) => {
    if (handleRoleCheck()) return;
    try {
      const [ok, data] = isWidgetTab
        ? await API.deleteWidgetImage(id, !!options.force)
        : await API.deleteImage(id, !!options.force);
      if (ok) {
        setImages((prev) => prev.filter((img) => img.id !== id));
        enqueueSnackbar('Asset deleted', { variant: 'success' });
        return { success: true };
      } else {
        const message = data?.error || data?.message || 'Delete failed';
        const msgLower = message?.toLowerCase() || '';
        const isInUse = msgLower.includes('in use') || msgLower.includes('currently in use') || msgLower.includes('popup') || msgLower.includes('widget') || data?.in_use;
        if (isInUse && !options.force) {
          return { inUse: true, message };
        }
        enqueueSnackbar(message, { variant: 'error' });
        return { success: false };
      }
    } catch {
      enqueueSnackbar('Delete failed', { variant: 'error' });
      return { success: false };
    }
  };

  const activeTabConfig = TAB_CONFIG.find((t) => t.key === activeTab);

  return (
    <Page>
      {/* Sub-nav + count */}
      <TabBar>
        <TabNav>
          {TAB_CONFIG.map((tab) => (
            <TabItem
              key={tab.key}
              $active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </TabItem>
          ))}
        </TabNav>
        {!loading && images.length > 0 && (
          <CountBadge>{images.length} {activeTabConfig?.label.toLowerCase()}</CountBadge>
        )}
      </TabBar>

      {/* Upload zone */}
      <UploadZone
        $dragging={dragging}
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <UploadIconWrap>
          {uploading ? <AppLoader visible size="small" /> : <CloudUploadOutlinedIcon />}
        </UploadIconWrap>
        <UploadText>
          <UploadMain>
            {uploading
              ? 'Uploading…'
              : <>{dragging ? 'Drop it here!' : <><span>Browse</span> or drag an image here</>}</>
            }
          </UploadMain>
          <UploadHint>PNG, JPG, GIF, SVG · Max 1 MB · Recommended min 800 × 600 px</UploadHint>
        </UploadText>
        <HiddenInput ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInput} />
      </UploadZone>

      {loading && <AppLoader visible />}

      {!loading && images.length > 0 && (
        <Grid>
          {images.map((image) => (
            <ImageCard key={image.id} image={image} onDelete={handleDelete} />
          ))}
        </Grid>
      )}

      {!loading && images.length === 0 && (
        <EmptyState>
          <CloudUploadOutlinedIcon />
          <p>No {activeTabConfig?.label.toLowerCase()} yet. Upload your first one above.</p>
        </EmptyState>
      )}
    </Page>
  );
}
